import * as signalR from '@microsoft/signalr';
import { ITokenService } from '../../tokenService';
import { setSessionId, setSessionOwner, setRecentReasoningNotifications, addEvaluationFeedback, store } from '../../utils/store';

export interface StopChatRPCProps {
    sessionId: string;
    currentUserInput: string;
    recentNotificationMessages: string[];
}

export interface DisplayEvaluationFeedbackProps {
    evaluationType: 'unsure' | 'mismatch' | 'info';
    message: string;
    sessionId: string;
}

const tokenScope: string =
    'api://7c720fd5-1d2c-4726-83ce-3a3b4b01d96e/access_as_user';

export class HubManager {
    private static _instance: HubManager;
    private _connection?: signalR.HubConnection;
    static lastUrl: string = "";
    private static dispatch?: (action: any) => void;
    private tokenService: ITokenService;
    private connectionFailures: number = 0;
    private maxConnectionFailures: number = 3;
    private isConnectionAvailable: boolean = true;
    private reconnectTimeout?: NodeJS.Timeout;

    constructor(tokenService: ITokenService) {
        this.tokenService = tokenService;
    }

    static getInstance(tokenService: ITokenService): HubManager {
        if (!HubManager._instance) {
            HubManager._instance = new HubManager(tokenService);
        }
        return HubManager._instance;
    }

    static setDispatch(dispatch: (action: any) => void): void {
        HubManager.dispatch = dispatch;
    }

    get connectionState(): signalR.HubConnectionState | undefined {
        return this._connection?.state;
    }

    async initializeConnection(url: string): Promise<void> {
        if (url.endsWith('/')) {
            url = url.slice(0, -1); // Remove trailing slash if present
        }

        // If we've exceeded max failures, don't attempt connection
        if (this.connectionFailures >= this.maxConnectionFailures) {
            if (this.isConnectionAvailable) {
                console.warn('SignalR: Server appears to be unavailable. Real-time features disabled.');
                this.isConnectionAvailable = false;
            }
            return;
        }

        // Stop existing connection if it exists
        if (this._connection) {
            await this.stopConnection();
        }

        this._connection = new signalR.HubConnectionBuilder()
                .withUrl(`${url}/hub`, {
                    withCredentials: false,
                    accessTokenFactory: async () => await this.tokenService.getAccessToken([tokenScope]) || "",
                    transport: signalR.HttpTransportType.LongPolling, // defaulting to LongPolling until we figure out the auth with WebSockets and SSE
                })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    // Custom exponential backoff strategy
                    if (retryContext.previousRetryCount < 3) {
                        return Math.pow(2, retryContext.previousRetryCount) * 1000;
                    } else {
                        return null; // Stop retrying after 3 attempts
                    }
                }
            })
            .build();

        this.setupEventHandlers();
        await this.startConnection()
            .catch(error => {
                this.handleConnectionError(error);
            });
    }

    private async startConnection(): Promise<void> {
        if (!this._connection) {
            throw new Error('Connection not initialized');
        }

        try {
            await this._connection.start();
            // Reset failure count on successful connection
            this.connectionFailures = 0;
            this.isConnectionAvailable = true;
            console.log('SignalR connection established');
        } catch (error) {
            throw error;
        }
    }

    private handleConnectionError(error: any): void {
        this.connectionFailures++;
        
        if (this.connectionFailures >= this.maxConnectionFailures) {
            if (this.isConnectionAvailable) {
                console.warn('SignalR: Unable to connect to server. Real-time features will be unavailable.');
                this.isConnectionAvailable = false;
            }
        } else {
            // Log only the first few failures to avoid spam
            if (this.connectionFailures === 1) {
                console.warn('SignalR: Connection attempt failed. Will retry...', error.message || error);
            }
        }
    }

    public resetConnectionFailures(): void {
        this.connectionFailures = 0;
        this.isConnectionAvailable = true;
    }

    /**
     * Attempts to reconnect if connection is down.
     * Call this before making chat requests to ensure SignalR is available.
     */
    public async ensureConnection(url: string): Promise<void> {
        // If connection exists and is connected, nothing to do
        if (this._connection && this._connection.state === signalR.HubConnectionState.Connected) {
            return;
        }

        // If we're below max failures, reset and try to reconnect
        if (this.connectionFailures < this.maxConnectionFailures) {
            console.log('SignalR: Attempting to establish connection for chat request...');
            await this.initializeConnection(url);
        }
    }

    async stopConnection(): Promise<void> {
        if (!this._connection) {
            console.error('Connection not initialized');
            return;
        }

        try {
            // Remove all event handlers before stopping
            this._connection.off('DisplayChatMessage');
            this._connection.off('Connected');
            this._connection.off('DisplayEvaluationFeedback');
            
            await this._connection.stop();
            this._connection = undefined; // Clear the connection
        } catch (error) {
            console.error('Error stopping SignalR connection:', error);
        }
    }

    private setupEventHandlers(): void {
        if (!this._connection) {
            console.error('Cannot setup event handlers: connection not initialized');
            return;
        }

        // Remove any existing handlers first to prevent duplicates
        this._connection.off('DisplayChatMessage');
        this._connection.off('Connected');
        this._connection.off('DisplayEvaluationFeedback');
        this._connection.off('SetSessionId');

        this._connection.onreconnecting(() => {
            if (this.isConnectionAvailable) {
                console.warn('SignalR: Connection lost, attempting to reconnect...');
            }
        });

        this._connection.onreconnected(() => {
            console.log('SignalR: Connection reestablished');
            this.connectionFailures = 0;
            this.isConnectionAvailable = true;
        });

        this._connection.onclose((error) => {
            if (error) {
                this.handleConnectionError(error);
            }
        });

        // SignalR Hub methods
        this._connection.on('DisplayChatMessage', ({message, sessionId, isProcessNotification}: {message: string, sessionId: string, isProcessNotification?: boolean}) => {
            if (HubManager.dispatch == undefined){
                return;
            }

            // Pass the message and isProcessNotification flag to the store
            HubManager.dispatch(setRecentReasoningNotifications({
                message,
                isProcessNotification
            }));
        });

        this._connection.on('Connected', ({userName, connectionId, timestamp}: {userName: string, connectionId: string, timestamp: number}) => {
            console.log('Session started: ', userName);
        });

        this._connection.on("SetSessionId", ({userName, sessionId}: {userName: string, sessionId: string}) => {
            if (HubManager.dispatch == undefined){
                console.warn("Streaming unavailable, dispatch is undefined");
                return;
            }

            HubManager.dispatch(setSessionId(sessionId));
            HubManager.dispatch(setSessionOwner(userName));
        });

        this._connection.on('DisplayEvaluationFeedback', ({evaluationType, message, sessionId}: {evaluationType: 'unsure' | 'mismatch' | 'info', message: string, sessionId: string}) => {
            if (HubManager.dispatch == undefined){
                console.warn("Evaluation feedback unavailable, dispatch is undefined");
                return;
            }

            HubManager.dispatch(addEvaluationFeedback({
                evaluationType,
                message,
                sessionId
            }));
        });
    }

    async stopChatExecution(props: StopChatRPCProps): Promise<void> {
        const { sessionId, currentUserInput, recentNotificationMessages } = props;
        if (!this._connection) {
            console.error('Cannot stop chat execution: connection not initialized');
            return;
        }

        if (this._connection.state !== signalR.HubConnectionState.Connected) {
            console.error('Cannot stop chat execution: connection not established');
            return;
        }

        try {
            await this._connection.invoke('StopChatExecution', sessionId, currentUserInput, recentNotificationMessages);
        } catch (error) {
            console.error('Error calling StopChatExecution:', error);
            throw error;
        }
    }
}
